'use client'

import * as React from 'react'
import { type VariantProps } from 'class-variance-authority'
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group'
import { Toggle as TogglePrimitive } from '@base-ui/react/toggle'

import { cn } from '@/registry/reapit/lib/utils'
import { toggleVariants } from '@/registry/reapit/ui/toggle'

/*
 * ToggleGroup — migrated from Radix to Base UI
 *
 * API changes:
 *   Radix:   ToggleGroupPrimitive.Root with type="single"|"multiple"
 *   Base UI: ToggleGroupPrimitive with multiple boolean (default false = single)
 *
 *   Radix:   ToggleGroupPrimitive.Item
 *   Base UI: TogglePrimitive (each toggle inside the group)
 *
 *   Radix onValueChange:
 *     single:   (value: string) => void
 *     multiple: (value: string[]) => void
 *   Base UI: (groupValue: string[], eventDetails) => void
 *
 * We preserve the Radix `type` prop in our public API for backwards
 * compatibility. Internally, `type="multiple"` maps to `multiple={true}`.
 *
 * Data attribute changes:
 *   data-state="on"  → data-pressed
 *   data-state="off" → (no attribute)
 *
 * DS alignment (Phase 3A):
 *   - spacing=0 (default): items joined edge-to-edge like ButtonGroup
 *   - spacing>0: items separated with gap, each with own border-radius
 *   - Focus ring: ring-ring/50 = border-focus (purple-300)
 *   - Pressed/selected: bg-accent (fill-action-lightest = purple-050)
 */

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & {
    spacing?: number
  }
>({
  size: 'default',
  variant: 'default',
  spacing: 0,
})

export namespace ToggleGroup {
  export interface Props
    extends
      Omit<React.ComponentProps<typeof ToggleGroupPrimitive>, 'onValueChange' | 'render'>,
      VariantProps<typeof toggleVariants> {
    /** Radix-compat: "single" or "multiple". Maps to Base UI `multiple`. */
    type?: 'single' | 'multiple'
    /** Radix-compat: callback for single mode. */
    onValueChange?: ((value: string) => void) | ((value: string[]) => void)
    spacing?: number
  }
}

function ToggleGroup({
  className,
  variant,
  size,
  spacing = 0,
  type = 'single',
  children,
  onValueChange,
  value,
  defaultValue,
  ...props
}: ToggleGroup.Props) {
  const multiple = type === 'multiple'

  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      style={{ '--gap': spacing } as React.CSSProperties}
      className={cn(
        'group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-md data-[spacing=default]:data-[variant=outline]:shadow-xs',
        className,
      )}
      multiple={multiple}
      value={value as readonly string[] | undefined}
      defaultValue={defaultValue as readonly string[] | undefined}
      onValueChange={
        onValueChange
          ? (groupValue) => {
              if (multiple) {
                ;(onValueChange as (value: string[]) => void)(groupValue as string[])
              } else {
                // Single mode: Radix called with the single string value (or "" to deselect)
                const val = groupValue as string[]
                const lastVal = val.length > 0 ? val[val.length - 1] : ''
                ;(onValueChange as (value: string) => void)(lastVal)
              }
            }
          : undefined
      }
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size, spacing }}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  )
}

export namespace ToggleGroupItem {
  export interface Props
    extends
      Omit<React.ComponentProps<typeof TogglePrimitive>, 'onPressedChange' | 'render'>,
      VariantProps<typeof toggleVariants> {}
}

function ToggleGroupItem({ className, children, variant, size, ...props }: ToggleGroupItem.Props) {
  const context = React.useContext(ToggleGroupContext)

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      data-spacing={context.spacing}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        'w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10',
        'data-[spacing=0]:rounded-none data-[spacing=0]:shadow-none data-[spacing=0]:first:rounded-l-md data-[spacing=0]:last:rounded-r-md data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l',
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  )
}

export { ToggleGroup, ToggleGroupItem }
